import type { AppSettings, DudaAccount, DudaIntegration } from './types';

export const DUDA_ACCOUNT_PRIMARY_ID = 'duda-a1';

export function newDudaAccountId(): string {
  return `duda-${crypto.randomUUID().slice(0, 8)}`;
}

export function createDudaAccount(label?: string): DudaAccount {
  return {
    id: newDudaAccountId(),
    label: label?.trim() || 'New account',
    apiUser: '',
    apiPassword: '',
    siteName: '',
  };
}

export function defaultDudaIntegration(): DudaIntegration {
  return {
    activeAccountId: DUDA_ACCOUNT_PRIMARY_ID,
    accounts: [
      {
        id: DUDA_ACCOUNT_PRIMARY_ID,
        label: 'Primary account',
        apiUser: '',
        apiPassword: '',
        siteName: '',
      },
    ],
  };
}

/** Merge stored settings; migrate legacy flat `apiUser`/`apiPassword` into first account. */
export function normalizeDudaIntegration(parsed: unknown, base: DudaIntegration): DudaIntegration {
  if (!parsed || typeof parsed !== 'object') {
    return base;
  }
  if ('accounts' in parsed) {
    const p = parsed as DudaIntegration;
    if (Array.isArray(p.accounts) && p.accounts.length > 0) {
      const accounts = p.accounts.map((a, i) => ({
        id: a.id?.trim() || (i === 0 ? DUDA_ACCOUNT_PRIMARY_ID : newDudaAccountId()),
        label: a.label?.trim() || `Account ${i + 1}`,
        apiUser: a.apiUser ?? '',
        apiPassword: a.apiPassword ?? '',
        siteName: a.siteName ?? '',
      }));
      const active =
        p.activeAccountId && accounts.some((a) => a.id === p.activeAccountId)
          ? p.activeAccountId
          : accounts[0].id;
      return { accounts, activeAccountId: active };
    }
  }

  const old = parsed as { apiUser?: string; apiPassword?: string; siteName?: string } | null;
  return {
    accounts: [
      {
        id: DUDA_ACCOUNT_PRIMARY_ID,
        label: 'Primary account',
        apiUser: old?.apiUser ?? '',
        apiPassword: old?.apiPassword ?? '',
        siteName: old?.siteName ?? '',
      },
    ],
    activeAccountId: DUDA_ACCOUNT_PRIMARY_ID,
  };
}

export function getActiveDudaAccount(settings: AppSettings): DudaAccount {
  const d = settings.integrations.duda;
  const acc = d.accounts.find((a) => a.id === d.activeAccountId) ?? d.accounts[0];
  if (acc) {
    return acc;
  }
  return defaultDudaIntegration().accounts[0];
}
